import React, { useEffect, useRef, useState } from 'react';
import { DEPARTMENTS, titleCase } from '../constants';
import { hapticSuccess, hapticWarning } from '../utils/haptics';
import './QuickAddSheet.css';

/**
 * Bottom sheet for adding one-time items to the shopping list.
 * Stays open after each add so several items can be entered in a row --
 * the department stays sticky between adds, the name/qty/unit clear.
 */
function QuickAddSheet({ isOpen, onClose, onAdd }) {
  const [name, setName] = useState('');
  const [department, setDepartment] = useState('grocery');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [justAdded, setJustAdded] = useState([]);
  const nameInputRef = useRef(null);

  // Close on Escape and lock body scroll while the sheet is open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Reset the session each time the sheet opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setQuantity('');
      setUnit('');
      setJustAdded([]);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      hapticWarning();
      nameInputRef.current?.focus();
      return;
    }

    hapticSuccess();
    onAdd({
      item: trimmed,
      category: department,
      quantity: quantity.trim(),
      quantity_type: unit.trim()
    });

    setJustAdded(prev => [...prev, trimmed]);
    setName('');
    setQuantity('');
    setUnit('');
    nameInputRef.current?.focus();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  return (
    <>
      <div className="quick-add-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="quick-add-sheet" role="dialog" aria-label="Add one-time item">
        <div className="quick-add-grabber" />

        <div className="quick-add-header">
          <h3>Add one-time item</h3>
          <button onClick={onClose} className="quick-add-close" aria-label="Close">
            ✕
          </button>
        </div>

        <input
          ref={nameInputRef}
          type="text"
          className="quick-add-name"
          placeholder="Item name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyPress={handleKeyPress}
          autoFocus
        />

        <div className="quick-add-chips">
          {DEPARTMENTS.map(dept => (
            <button
              key={dept}
              type="button"
              className={`quick-add-chip ${department === dept ? 'selected' : ''}`}
              onClick={() => setDepartment(dept)}
            >
              {titleCase(dept)}
            </button>
          ))}
        </div>

        <div className="quick-add-row">
          <input
            type="text"
            className="quick-add-qty"
            placeholder="Qty"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            onKeyPress={handleKeyPress}
            inputMode="decimal"
          />
          <input
            type="text"
            className="quick-add-unit"
            placeholder="Unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>

        {justAdded.length > 0 && (
          <div className="quick-add-recent">
            <span className="quick-add-recent-label">Just added</span>
            <ul>
              {justAdded.map((added, index) => (
                <li key={index}>✓ {added}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="quick-add-actions">
          <button onClick={handleAdd} className="quick-add-submit">
            Add
          </button>
          <button onClick={onClose} className="quick-add-done">
            Done
          </button>
        </div>
      </div>
    </>
  );
}

export default QuickAddSheet;
